import React from "react";
import Div100vh from "react-div-100vh";

import Header from "@/b2b/components/Header";

/**
 * The shell every "the journey has stopped here" screen uses.
 *
 * Purely presentational — it owns the layout and nothing else, so a screen that needs to fetch,
 * escalate or navigate does that itself and hands the result in. Extracted because there were
 * four of these pages, all built by copying the last one, and they had drifted into four
 * different products: a purple gradient card on a patterned background, a hardcoded blue button
 * that overrode the partner's colour, `bg-[${theme?.bgColor}]` classes Tailwind cannot generate
 * at runtime and so never applied at all, and an emoji headline.
 *
 * The look is the one the rest of the journey already uses — plain themed background, partner
 * logo on white, a quiet brand-tinted badge instead of a full-bleed panel. A tenant reaching one
 * of these is usually anxious, and a page that suddenly looks like a different app reads as
 * "something broke" before a single word is read.
 *
 * @param {object} theme    partner theme (background, text, heading, tint)
 * @param {node}   icon     lucide icon element for the badge
 * @param {string} title    one short line — what happened
 * @param {string} body     one short paragraph — what happens next
 * @param {node}   children actions, rendered under the copy
 */
export default function JourneyMessageScreen({
  theme,
  icon,
  title,
  body,
  children,
}) {
  return (
    <Div100vh
      className={`flex flex-col ${theme?.backgroundColor} ${theme?.textColor}`}
    >
      <div className="px-2 pt-5">
        <Header showBackButton={false} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-sm flex flex-col items-center">
          <div
            className={`${theme?.tint || "bg-black/5"} flex h-14 w-14 items-center justify-center rounded-full`}
          >
            {icon}
          </div>

          <h1
            className={`${theme?.headingColor} mt-5 text-center text-[22px] font-semibold leading-tight`}
          >
            {title}
          </h1>

          <p
            className={`${theme?.subHeadingColor} mt-2 text-center text-sm leading-relaxed`}
          >
            {body}
          </p>

          {children}
        </div>
      </div>
    </Div100vh>
  );
}

/**
 * The pill button these screens use for a secondary action, so WhatsApp / Call / Try again /
 * Back all match. Renders an anchor when given `href`, a button otherwise.
 */
export function JourneyPillAction({ theme, href, onClick, icon, label, external }) {
  const className = `${theme?.textColor} flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border border-black/15 bg-white text-sm font-medium transition-colors hover:bg-black/[0.03] active:bg-black/[0.06]`;

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={className}
        aria-label={label}
        {...(external
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {icon}
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} aria-label={label}>
      {icon}
      {label}
    </button>
  );
}
